<template lang="pug">
section#ministry-page.section.page-section.top-section
  spinner(v-if="settings.loading")
  div(v-if="!settings.loading")
    .ministry-header(:style="`background-image:url(${backgroundUrl})`")
      .container
        cl-img.avatar(
          v-if="ministry._embedded.profile_image.id"
          :cl-id="ministry._embedded.profile_image.cl_id"
          :transform="{ width: 250, height: 250, crop: 'thumb' }"
        )
        h1.is-size-1 {{ ministry.name }}
        p.collection-desc.is-size-5(v-if="ministry.age_range") {{ ministry.age_range }}
    section.section.page-section
      .ministry-body.container
        .blocks
          .block.is-even
            .block-image
              image-gallery(
                :value="ministry._embedded.main_gallery"
                :transform="{ width: 640, height: 360, crop: 'fill' }"
              )
            .block-details
              .content.mobile-padding
                h3.is-size-4 {{ ministry.main_heading || `About ${ministry.name}` }}
                span.is-size-5(v-html="compiledMainBody")
          .block.is-odd
            .block-image
              image-gallery(
                :value="ministry._embedded.service_gallery"
                :transform="{ width: 640, height: 360, crop: 'fill' }"
              )
            .block-details
              .content.mobile-padding
                h3.is-size-4 {{ ministry.service_heading || 'Service times' }}
                p.is-size-5(v-html="compiledServiceBody")
              .service-list.mobile-padding
                service-list-item(
                  v-for="service in ministry._embedded.services"
                  :key="service.id"
                  :value="service"
                )
          .block.event-block(
            v-for="(event, $index) in events"
            v-if="!isMobile"
            :class="{ 'is-even': $index % 2 === 0, 'is-odd': $index % 2 !== 0 }"
          )
            .block-image
              router-link(:to="{ name: 'event', params: { id: event.key } }")
                cl-img(
                  v-if="event._embedded.artwork"
                  :cl-id="event._embedded.artwork.cl_id"
                  :transform="{ width: 640, height: 360 }"
                )
            .block-details
              .content.mobile-padding
                //- b-tag(type="is-dark") Special Event
                h3.is-size-4
                  router-link(:to="{ name: 'event', params: { id: event.key } }") {{ event.title }}
                p.is-size-5
                  event-date.has-text-grey(
                    format="dddd MMMM Do"
                    :limit="1"
                    :value="event"
                  )
                p.is-size-5 {{ event.short_desc }}
                router-link.button.is-primary(
                  v-if="event.registration_required"
                  :to="{ name: 'event', params: { id: event.key } }"
                )
                  span Sign Up
                  b-icon(icon="arrow-right")
          .mobile-events.columns(v-if="isMobile")
            .column.is-one-third(
              v-for="(event, $index) in events"
            )
              event-list-item(
                :value="event"
                :limit="2"
              )
          .map-block.block(:class="{ 'is-even': events.length % 2 === 0, 'is-odd': events.length % 2 !== 0 }")
            .block-image
              google-map(
                :location="account._embedded.location"
                :name="account._embedded.location.id"
              )
            .block-details
              .content.mobile-padding
                h3.is-size-4 {{ ministry.name }}
                p.is-size-5(v-html="account._embedded.location.address.html")
                ministry-links(
                  v-if="ministry.email || ministry.phone_number"
                  :value="ministry"
                  :links="['email', 'phone_number']"
                )
                social-links(:value="ministry.social")
        //- .content-block
        //-   .block-header.mobile-padding
        //-     h3.is-size-5 Upcoming events
        //-   .columns.is-multiline
        //-     .column.is-one-third(
        //-       v-for="event in events"
        //-       :key="event.id"
        //-     )
        //-       event-list-item(
        //-         :value="event"
        //-       )
        //- .content-block
        //-   h3.is-size-5 Photos
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import marked from 'marked';
import cloudinary from 'cloudinary-core';
import moment from 'moment';
import EpisodeListItem from '@/components/episodes/EpisodeListItem.vue';
import EpisodeTypeahead from '@/components/episodes/EpisodeTypeahead.vue';
import EventDate from '@/components/events/EventDate.vue';
import EventListItem from '@/components/events/EventListItem.vue';
import GoogleMap from '@/components/GoogleMap.vue';
import ImageGallery from '@/components/ImageGallery.vue';
import MinistryLinks from '@/components/ministries/MinistryLinks.vue';
import ReadMore from '@/components/ReadMore.vue';
import ServiceListItem from '@/components/ministries/ServiceListItem.vue';
import SocialLinks from '@/components/SocialLinks.vue';
import Spinner from '@/components/Spinner.vue';
import * as actionTypes from '@/store/types/actions';
import * as getterTypes from '@/store/types/getters';
import * as mutationTypes from '@/store/types/mutations';

export default {
  name: 'ministry',
  components: {
    EpisodeListItem,
    EpisodeTypeahead,
    EventDate,
    EventListItem,
    GoogleMap,
    ImageGallery,
    MinistryLinks,
    ReadMore,
    ServiceListItem,
    SocialLinks,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getterTypes.GET_ACCOUNT,
      ministry: getterTypes.GET_MINISTRY,
      episodes: getterTypes.GET_EPISODES,
      events: getterTypes.GET_EVENTS,
    }),
    backgroundUrl() {
      if (!this.ministry._embedded.header_image) return null;
      let opts = {};
      if (this.isMobile) {
        opts = {
          width: 600,
          height: 600,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'thumb',
          opacity: 30,
          format: 'png',
        };
      } else {
        opts = {
          width: 2000,
          height: 700,
          effect: 'blur:700',
          quality: 'auto',
          fetch_format: 'auto',
          crop: 'fill',
          opacity: 30,
          format: 'png',
        };
      }
      return this.cloudinary.url(this.ministry._embedded.header_image.cl_id, opts);
    },
    compiledMainBody() {
      if (this.ministry.id === undefined || !this.ministry.main_body) return null;
      return marked(this.ministry.main_body);
    },
    compiledServiceBody() {
      if (this.ministry.id === undefined || !this.ministry.service_body) return null;
      return marked(this.ministry.service_body);
    },
    episodesQuery() {
      const from = this.pagination.per * (this.pagination.page - 1);
      const payload = {
        size: this.pagination.per,
        from: from >= 0 ? from : 0,
        sort: {
          pub_date: { order: 'desc' },
          'title.raw': { order: 'asc' },
        },
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { match: { is_published: true } },
              { terms: { channel_ids: this.ministry.channel_ids } },
            ],
          },
        },
      };
      return payload;
    },
    eventsQuery() {
      const today = moment().format('YYYY-MM-DD');
      const payload = {
        sort: [
          { end_date: 'asc' },
          { 'title.raw': 'asc' },
          '_score',
        ],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { terms: { ministry_ids: [this.ministry.id] } },
              { match: { is_published: true } },
              { match: { type: 'event' } },
              { range: { pub_date: { lte: today } } },
              { range: { end_date: { gte: today } } },
            ],
          },
        },
      };
      return payload;
    },
    isMobile() {
      return window.innerWidth <= 769;
    },
  },
  async created() {
    this.settings.loading = true;
    this.cloudinary = cloudinary.Cloudinary.new({
      cloud_name: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
    });
    this.$store.commit(mutationTypes.SET_EVENTS, []);
    await this.$store.dispatch(actionTypes.DO_GET_MINISTRY, {
      id: this.id,
      zoom: 'header_image,main_gallery,profile_image,roles,services,service_gallery',
    });
    this.getPage();
  },
  data() {
    return {
      cloudinary: null,
      pagination: {
        page: 1,
        per: 24,
        total: 0,
      },
      settings: {
        loading: false,
        revealLeft: {
          distance: '25px',
          delay: 250,
          origin: 'left',
        },
        revealRight: {
          distance: '25px',
          delay: 250,
          origin: 'right',
        },
      },
    };
  },
  methods: {
    async getPage(page) {
      const self = this;
      this.pagination.page = page;
      const episodesResponse = await this.$store.dispatch(actionTypes.DO_GET_EPISODES, this.episodesQuery);
      self.pagination.total = episodesResponse.hits.total;
      await this.$store.dispatch(actionTypes.DO_GET_EVENTS, this.eventsQuery);
      this.settings.loading = false;
    },
  },
  props: [
    'id',
  ],
};
</script>

<style lang="scss">
#ministry-page {
  @include blocks;
  .ministry-header {
    @include hero;
    .avatar {
      width: 125px;
      height: 125px;
      border-radius: 100px;
      display: inline;
      margin-bottom: 0.75rem;
    }
    h1 {
      margin-bottom: 1rem;
    }
  }
  .event-block {
    .block-image {
      @include blockImage;
    }
  }
  .google-map {
    min-height: 315px;
  }
  .ministry-links {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: $tablet) {
  #ministry-page {
    .ministry-header {
      background-size: cover;
      background-attachment: inherit;
    }
    .mobile-events {
      margin-top: 0.75rem;
      margin-bottom: 1rem;
      @include carousel;
      // .event-list-item {
      //   min-width: 280px;
      //   width: 280px;
      //   padding: 0.75rem;
      // }
    }
    .map-block {
      .block-image {
        width: 100%;
      }
    }
  }
}
</style>
